import React, { createElement, RefObject, useEffect, useImperativeHandle, useMemo, useRef, useState, memo, useCallback } from 'react';
import {
  View,
  StyleSheet,
  TextInput,
  NativeSyntheticEvent,
  TextInputFocusEventData,
  StyleProp,
  ViewStyle,
  TextStyle,
  Animated,
  Text,
  TouchableOpacity,
  Platform,
  ViewProps,
  TextInputProps,
  TextInputKeyPressEventData,
  TextInputSubmitEditingEventData,
  ReturnKeyTypeOptions,
} from 'react-native';
import { COLORS, textStyles } from './constants';
import { mbApplyTransparency, mbPlatformStyle } from '@/utils/general/style-utils';
import { MB_Button } from '@/components/general/button';
import { isMobile } from '@/utils/general/device-info';
import { MB_SVG_Eye, MB_SVG_EyeOff } from '@/components/general/svg-components';
import { MB_Portal } from '@/components/general/portal';
import { MB_TextInputToolTipPortalRef } from './MB_TextInputPortal';
import { TOOLTIP_POSITION } from '@/components/general/tool-tip';
import { Masks, formatWithMask, Mask } from 'react-native-mask-input';

interface MB_TextInputRef {
  /**
   * Focuses the text input.
   */
  focus: () => void;

  /**
   * Blurs the text input.
   */
  blur: () => void;
}

enum MB_TextInputInputType {
  numbers = 'numbers',
  positiveNumbers = 'positiveNumbers',
  float = 'number',
  positiveFloat = 'positiveFloat',
}

interface CharacterCount {
  /**
   * The minimum characters length.
   */
  minLength?: number;

  /**
   * The maximum characters length.
   */
  maxLength?: number;

  /**
   * The current length.
   */
  currentLength: number;

  /**
   * If true, the character limit will be displayed.
   */
  showCharacterLimit?: boolean;

  /**
   * If true, allows multiline input.
   */
  multiline?: boolean;
}

const CharacterCount = ({ minLength, maxLength, currentLength, showCharacterLimit, multiline }: CharacterCount) => {
  if (showCharacterLimit || (multiline && showCharacterLimit !== false)) {
    if (minLength) {
      const numCharactersLeftToType = minLength - currentLength;

      if (currentLength === 0) {
        return <Text style={styles.remainingCharactersText}>{`Enter at least ${minLength} character${minLength > 1 ? 's' : ''}`}</Text>;
      } else if (numCharactersLeftToType > 0) {
        return <Text style={styles.remainingCharactersText}>{`${numCharactersLeftToType} more to go...`}</Text>;
      }
    }

    if (maxLength) {
      const remainingCaracters = maxLength - currentLength;
      if (currentLength === 0) {
        return <Text style={styles.remainingCharactersText}>{`Enter up to ${maxLength} characters`}</Text>;
      } else if (remainingCaracters > 0) {
        return <Text style={styles.remainingCharactersText}>{`${remainingCaracters} character${remainingCaracters > 1 ? 's' : ''} left`}</Text>;
      }
    }

    // Added this to prevent the UI from jumping up when no character limit text is displayed
    return <Text style={styles.remainingCharactersText} />;
  }

  return null;
};

interface MB_TextInputDefaultProps {
  /**
   * General style.
   */
  style?: StyleProp<ViewStyle>;

  /**
   * Style to be applied when the text input is focused.
   */
  styleAdditionsFocused?: StyleProp<ViewStyle>;

  /**
   * Style to be applied to the text input.
   */
  textInputStyle?: StyleProp<TextStyle>;

  /**
   * Style to be applied to the permanent leading text.
   */
  permanentLeadingTextStyle?: StyleProp<TextStyle>;

  /**
   * Style to be applied to the right title.
   */
  rightTitleStyle?: StyleProp<TextStyle>;

  /**
   * Style to be applied to the asterisk symbol indicating
   * a required field.
   */
  requiredAsteriksStyle?: StyleProp<TextStyle>;

  /**
   * Style to be applied to the title.
   */
  titleStyle?: StyleProp<TextStyle>;

  /**
   * Color of the placeholder text.
   */
  placeholderTextColor?: string;
}

type FormattedWithMaskType = {
  masked: string;
  unmasked: string;
  obfuscated: string;
};

interface MB_TextInputProps extends MB_TextInputDefaultProps {
  /**
   * A reference to the text input.
   */
  MB_Ref?: RefObject<MB_TextInputRef | null>;

  /**
   * Placeholder text.
   */
  placeholder?: string;

  /**
   * Callback that is called when the text input receives focus.
   */
  onFocus?: ((e: NativeSyntheticEvent<TextInputFocusEventData>) => void) | undefined;

  /**
   * Callback that is called when the text input loses focus.
   */
  onBlur?: ((e: NativeSyntheticEvent<TextInputFocusEventData>) => void) | undefined;

  /**
   * Callback that is called when the text input's text changes.
   */
  onChangeText?: ((text: string) => void) | undefined;

  /**
   * Callback that is called when a key is pressed.
   */
  onKeyPressed?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;

  /**
   * Callback that is called when the text input's submit button is pressed.
   */
  onSubmitEditing?: ((e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => void) | undefined;

  /**
   * If true, the text field will blur when submitted. The default value is true.
   */
  blurOnSubmit?: boolean | undefined;

  /**
   * Title text to display above the text input on the left side.
   */
  title?: string;

  /**
   * Animates the title text when the text input gets and loses focus.
   * When set to true, the title will be inside the text input when the
   * text input is not focused and will move up once it receives focus.
   */
  animatedTitle?: boolean;

  /**
   * Text value.
   */
  value?: string;

  /**
   * If true, the text input obscures the text entered so that sensitive text
   * like passwords stay secure. The default value is false.
   */
  secureTextEntry?: boolean;

  /**
   * Wraps the textInput in form element for web and in view element for mobile.
   * Useful to avoid the browser warning `Password field is not contained in a form:` that is being
   * thrown when using secureTextEntry.
   */
  wrapInFormElement?: boolean;

  /**
   * If true, enables auto-correct. The default value is true.
   */
  autoCorrect?: boolean;

  /**
   * If true, focuses the input on componentDidMount. The default value is false.
   */
  autoFocus?: boolean;

  /**
   * The expected text content. This hints the system about what kind of keyboard
   * layout to show for this text input on mobile.
   */
  textContentType?:
    | 'none'
    | 'URL'
    | 'addressCity'
    | 'addressCityAndState'
    | 'addressState'
    | 'countryName'
    | 'creditCardNumber'
    | 'emailAddress'
    | 'familyName'
    | 'fullStreetAddress'
    | 'givenName'
    | 'jobTitle'
    | 'location'
    | 'middleName'
    | 'name'
    | 'namePrefix'
    | 'nameSuffix'
    | 'nickname'
    | 'organizationName'
    | 'postalCode'
    | 'streetAddressLine1'
    | 'streetAddressLine2'
    | 'sublocality'
    | 'telephoneNumber'
    | 'username'
    | 'password'
    | 'newPassword'
    | 'oneTimeCode';

  /**
   * If true, the text input can be multiple lines. The default value is false.
   */
  multiline?: boolean;

  /**
   * The maximum character length of the text input.
   */
  maxLength?: number;

  /**
   * The minimum character length of the text input.
   */
  minLength?: number;

  /**
   * If true, the text input will be in error state.
   */
  isError?: boolean;

  /**
   * In case the input is of type number, this filters out the invalid characters
   * when receiving the on change text event.
   */
  inputType?: MB_TextInputInputType;

  /**
   * A prefix text to always display before the text input.
   */
  permanentLeadingText?: string;

  /**
   * Tooltip details.
   *
   * Please take a look at {@link MB_TextInputToolTipPortal} to see the usage
   * instructions.
   */
  toolTipData?: {
    /**
     * If true, the tool tip will be shown when the text receives focus.
     */
    showOnFocus: boolean;

    /**
     * Reference to the portal component.
     */
    portalRef: RefObject<MB_TextInputToolTipPortalRef | null>;

    /**
     * Position of the tool tip relative to the text input.
     */
    moveBy?: { x: number; y: number };

    /**
     * Style of the tooltip's wrapper.
     */
    wrapperStyle?: StyleProp<ViewStyle>;

    /**
     * Element to display as the tooltip.
     */
    toolTipElement: React.ReactNode;

    /**
     * Position of the tooltip.
     */
    toolTipPosition?: TOOLTIP_POSITION;
  };

  /**
   * If true, the text input will be disabled.
   */
  disable?: boolean;

  /**
   * Title text to display above the text input on the right side.
   */
  rightTitle?: string;

  /**
   * If true, the character limit will be displayed.
   */
  showCharacterLimit?: boolean;

  /**
   * If true, display an asterisk indicating that the field is required.
   */
  showRequiredAsteriks?: boolean;

  /**
   * An icon to display on the left side of the text input.
   */
  leftIcon?: React.ReactNode;

  /**
   * Hints to the system's keyboard to automatically capitalize certain
   * characters.
   * - none: don't auto capitalize anything.
   * - sentences: first letter of each sentence (default).
   * - words: first letter of each word.
   * - characters: all characters.
   */
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  returnKeyType?: ReturnKeyTypeOptions;
  /**
   * A convinient way to set the next focus target after submitting on the current text field.
   * Does the following 3 operations unless any of the fields were already explicitly specified.
   *
   * * Sets the returnKeyType to next
   * * Sets blurOnFocus to false(to avoid flickering keyboard, keyboard preseists between blur and focus)
   * * Focuses on whatever text input ref was provided
   */
  nextToFocus?: React.RefObject<MB_TextInputRef | null>;
  mask?: {
    maskType: Mask;
    onChangeText: (withMask: FormattedWithMaskType) => void;
    obfuscationCharacter?: string;
  };
}

const MB_TextInputSetDefaultStyles = (props: MB_TextInputDefaultProps) => {
  defaultProps = props;
};

let defaultProps: MB_TextInputDefaultProps | undefined;

const Form = Platform.select({
  web: (props: TextInputProps) => createElement('form', props),
  default: (props: ViewProps) => createElement(View, props),
});

const _MB_TextInput = memo(
  ({
    MB_Ref,
    placeholder,
    onFocus,
    onBlur,
    onChangeText,
    onKeyPressed,
    onSubmitEditing,
    blurOnSubmit,
    style,
    styleAdditionsFocused,
    textInputStyle,
    title,
    animatedTitle,
    value,
    titleStyle,
    permanentLeadingText,
    permanentLeadingTextStyle,
    secureTextEntry,
    wrapInFormElement,
    autoCorrect,
    autoFocus,
    textContentType,
    multiline = false,
    maxLength,
    minLength,
    isError,
    placeholderTextColor,
    inputType,
    toolTipData,
    disable,
    rightTitle,
    rightTitleStyle,
    showCharacterLimit,
    showRequiredAsteriks,
    requiredAsteriksStyle,
    leftIcon,
    autoCapitalize,
    returnKeyType,
    nextToFocus,
    mask,
  }: MB_TextInputProps) => {
    const [isHidingPassword, setIsHidingPassword] = useState(true);
    const [isFocused, setIsFocused] = useState(false);
    const [errorTitleStyle, setErrorTitleStyle] = useState<TextStyle>({});
    const errorViewStyle: StyleProp<ViewStyle> = useMemo(() => {
      if (isError) {
        setErrorTitleStyle({ color: COLORS.errorColor });
        return { borderColor: COLORS.errorColor };
      } else {
        setErrorTitleStyle({});
        return {};
      }
    }, [isError]);

    const [titleTranslate] = useState(new Animated.ValueXY({ x: 0, y: 0 }));
    const [animatedFontSize] = useState(new Animated.Value(16));

    const textInputRef = useRef<TextInput>(null);
    const textInputWrapperRef = useRef<TouchableOpacity>(null);

    const [toolTipPosition, setToolTipPosition] = useState<{ left: number; top: number } | undefined>();

    /**
     * React Native Web exposes focusable prop but it does not exist in React Native. That is why we are
     * doing this weird conversion. At the end, this helps us to prevent the touchable opacity that wraps
     * the text input from gaining focus when pressing tab.
     *
     * Note: Doing the same is also possible with textInputWrapperRef.current?.setNativeProps({ tabIndex: -1 }); but
     * that was showing some warnings.
     */
    const touchableOpacityDisableFocus = useRef<ViewProps>({ focusable: false } as any);

    useImperativeHandle(MB_Ref, () => ({
      focus: () => textInputRef.current?.focus(),
      blur: () => textInputRef.current?.blur(),
    }));

    const handleToolTipFocus = () => {
      if (toolTipData?.showOnFocus) {
        textInputWrapperRef.current?.measureInWindow(async (x, y, _width, height) => {
          let portalPosition = { x: 0, y: 0 };
          if (toolTipData.portalRef.current) {
            portalPosition = await toolTipData.portalRef.current?.getPortalWindowMeasure();
          }
          if (toolTipData.toolTipPosition === TOOLTIP_POSITION.bottom || !toolTipData.toolTipPosition) {
            const left = x - portalPosition.x;
            const top = y - portalPosition.y + height + 4;
            setToolTipPosition({ left, top });
          } else if (toolTipData.toolTipPosition === TOOLTIP_POSITION.top) {
            const left = x - portalPosition.x;
            const top = y - portalPosition.y;
            setToolTipPosition({ left, top });
          } else if (toolTipData.toolTipPosition === TOOLTIP_POSITION.left) {
            const left = x - portalPosition.x - 4;
            const top = y - portalPosition.y;
            setToolTipPosition({ left, top });
          }
        });
      }
    };

    const onSubmitEditingIntenral = useCallback(
      (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
        if (onSubmitEditing) {
          onSubmitEditing(e);
        } else if (nextToFocus) {
          nextToFocus.current?.focus();
        }
      },
      [nextToFocus, onSubmitEditing]
    );

    function getTextInput(textStyleIncoming?: StyleProp<TextStyle>) {
      // TODO: There is an issue on android devices that break scrolling for multiline if textInput is disabled.
      // For that reason on android instead of disabling the text input, we will instead prevent it from focusing.
      // by calling blur inside onFocus if disable is true and platform is android.
      // https://github.com/facebook/react-native/issues/23117
      // Other possible solution - https://stackoverflow.com/questions/33060859/react-native-touchable-is-disabling-scrollview
      const disableFinal = Platform.OS === 'android' ? undefined : disable;

      const applyMask = (text: string): FormattedWithMaskType => {
        if (!mask || !mask.maskType) return { masked: text, unmasked: text, obfuscated: text };
        const { masked, unmasked, obfuscated } = formatWithMask({
          text: text,
          mask: mask.maskType,
          obfuscationCharacter: mask.obfuscationCharacter,
        });
        return {
          masked,
          unmasked,
          obfuscated,
        };
      };

      return (
        <TextInput
          editable={!disableFinal}
          ref={textInputRef}
          style={[multiline ? styles.multilineTextInputStyle : baseStyles.textInputStyle, styles.outlineRemover, textInputStyle, textStyleIncoming]}
          onFocus={event => {
            if (disable) {
              textInputRef.current?.blur();
            } else {
              setIsFocused(true);
              onFocus?.(event);
              handleToolTipFocus();
            }
          }}
          onBlur={event => {
            setIsFocused(false);
            onBlur?.(event);

            if (toolTipData?.showOnFocus) {
              setToolTipPosition(undefined);
            }
          }}
          placeholder={!animatedTitle || disable ? placeholder : ''}
          placeholderTextColor={placeholderTextColor ?? mbApplyTransparency(COLORS.lighterGray, 0.4)}
          onChangeText={newText => {
            if (disable) {
              return;
            }

            let filteredText = newText;
            if (inputType === MB_TextInputInputType.numbers) {
              filteredText = filteredText.match(/^[-|+]?\d*$/)?.[0] ?? value ?? '';
            } else if (inputType === MB_TextInputInputType.float) {
              filteredText = filteredText.match(/^[-|+]?\d*\.?\d*$/)?.[0] ?? value ?? '';
            } else if (inputType === MB_TextInputInputType.positiveNumbers) {
              filteredText = filteredText.replace(/[^0-9]/g, '');
            } else if (inputType === MB_TextInputInputType.positiveFloat) {
              filteredText = filteredText.replace(/[^0-9^.]/g, '');
            }

            if (mask && mask.maskType) {
              const withMask = applyMask(filteredText);
              mask.onChangeText(withMask);
            } else {
              onChangeText?.(filteredText);
            }
          }}
          onKeyPress={onKeyPressed}
          onSubmitEditing={onSubmitEditingIntenral}
          blurOnSubmit={blurOnSubmit ?? nextToFocus === undefined}
          value={value}
          secureTextEntry={secureTextEntry ? isHidingPassword : false}
          autoCorrect={autoCorrect}
          autoFocus={autoFocus}
          textContentType={textContentType}
          multiline={multiline}
          maxLength={maxLength}
          autoCapitalize={autoCapitalize}
          returnKeyType={returnKeyType ?? (nextToFocus === undefined ? undefined : 'next')}
        />
      );
    }

    const ToolTip = () => {
      if (!toolTipPosition) {
        return null;
      }
      const position = {
        left: toolTipPosition.left + (toolTipData?.moveBy?.x ?? 0),
        top: toolTipPosition?.top + (toolTipData?.moveBy?.y ?? 0),
      };

      return (
        <MB_Portal hostName={toolTipData?.portalRef.current?.getPortalName()}>
          <View style={[position, styles.toolTipWrapper, toolTipData?.wrapperStyle]}>{toolTipData?.toolTipElement}</View>
        </MB_Portal>
      );
    };

    useEffect(() => {
      let finalYtranslation = 0;
      let finalXTranslation = 0;
      if (isFocused || value || disable) {
        // Calculate the height of the view by looking either at the predefined container style height or passed in style height
        let height = Number(StyleSheet.flatten(style)?.height);
        height = isNaN(height) ? Number(StyleSheet.flatten(styles.container)?.height) : height;

        finalYtranslation = -(height / 2);
        finalXTranslation = -5;
      } else if (multiline) {
        // Try to place the container in the top corner for multiline views
        let height = Number(StyleSheet.flatten(style)?.height);
        height = isNaN(height) ? Number(StyleSheet.flatten(styles.container)?.height) : height;

        finalYtranslation = -(height / 2) + 25; // 25 here is the top padding
      }

      Animated.timing(titleTranslate, {
        toValue: { x: finalXTranslation, y: finalYtranslation },
        duration: 150,
        useNativeDriver: false,
      }).start();

      const finalFontSize = isFocused || value ? 10 : 16;
      Animated.timing(animatedFontSize, {
        toValue: finalFontSize,
        duration: 150,
        useNativeDriver: false,
      }).start();
    }, [animatedFontSize, style, disable, isFocused, multiline, titleTranslate, value]);

    const containerStyleMarginOnly = useMemo(() => {
      const combinedStyles = StyleSheet.flatten([style]);
      // Extract all the margin related stuff from combinedStyles
      // Create two separate styles here (both of type ViewStyle).
      const stylesMarginOnly: ViewStyle = {
        margin: combinedStyles.margin,
        marginHorizontal: combinedStyles.marginHorizontal,
        marginVertical: combinedStyles.marginVertical,
        marginTop: combinedStyles.marginTop,
        marginBottom: combinedStyles.marginBottom,
        marginLeft: combinedStyles.marginLeft,
        marginRight: combinedStyles.marginRight,
        width: combinedStyles.width,
      };

      return stylesMarginOnly;
    }, [style]);

    // Also get rid of all the as TextStyle
    // TODO: All of these used mbTextStyles before
    const rightTitleStyleCombined = useMemo(() => [styles.rightTitleStyle, rightTitleStyle] as TextStyle, [rightTitleStyle]);
    const permanentLeadingTextStyleCombined = useMemo(
      () => [textStyles.normalText, { textAlign: 'left' }, textInputStyle, permanentLeadingTextStyle] as TextStyle,
      [permanentLeadingTextStyle, textInputStyle]
    );
    const titleStyleCombined = useMemo(() => [styles.defaultTitleStyle, disable ? styles.disabledTitle : {}, titleStyle] as TextStyle, [disable, titleStyle]);
    const animatedTitleStyleCombined = useMemo(() => {
      return StyleSheet.flatten([
        {
          textAlign: 'left',
          color: isFocused ? COLORS.white : mbApplyTransparency(COLORS.lighterGray, 0.4),
          fontSize: 16,
          fontWeight: '500',
        },
        titleStyle,
        errorTitleStyle,
        {
          fontSize: Number(animatedFontSize),
        },
      ]) as TextStyle;
    }, [animatedFontSize, errorTitleStyle, isFocused, titleStyle]);

    return (
      <View style={containerStyleMarginOnly}>
        <View style={styles.titleContainer}>
          {!animatedTitle && title !== undefined && title !== '' && (
            <Text style={titleStyleCombined}>
              {title}
              {showRequiredAsteriks && <Text style={requiredAsteriksStyle ?? styles.requiredAsteriksColor}>*</Text>}
            </Text>
          )}
          {rightTitle !== undefined && rightTitle !== '' && <Text style={rightTitleStyleCombined}>{rightTitle}</Text>}
        </View>
        <TouchableOpacity
          {...touchableOpacityDisableFocus.current}
          ref={textInputWrapperRef}
          activeOpacity={1}
          onPress={() => {
            if (!disable) {
              textInputRef.current?.focus();
            }
          }}
          style={[
            styles.container,
            {
              paddingEnd: secureTextEntry ? 50 : 20,
              borderColor: isFocused ? COLORS.white : mbApplyTransparency(COLORS.lighterGray, 0.4),
              borderWidth: isFocused ? 2 : 1,
            },
            multiline && { paddingVertical: 10 },
            style,
            isFocused && styleAdditionsFocused,
            styles.marginNullified,
            errorViewStyle,
          ]}
        >
          <ToolTip />

          {multiline && <>{wrapInFormElement ? <Form>{getTextInput({ textAlignVertical: 'top' })}</Form> : <>{getTextInput({ textAlignVertical: 'top' })}</>}</>}

          {!multiline && (
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {leftIcon}

              {permanentLeadingText && (isFocused || value !== '') && <Text style={permanentLeadingTextStyleCombined}>{permanentLeadingText}</Text>}

              <View style={{ flex: 1 }}>{wrapInFormElement ? <Form>{getTextInput()}</Form> : <>{getTextInput()}</>}</View>
            </View>
          )}

          {secureTextEntry && (
            <MB_Button
              onPress={() => {
                setIsHidingPassword(!isHidingPassword);
              }}
              style={styles.securePasswordButton}
              rightElement={isHidingPassword ? <MB_SVG_EyeOff color={COLORS.lighterGray} size={16} /> : <MB_SVG_Eye color={COLORS.lighterGray} size={16} />}
            />
          )}

          {title && animatedTitle && (
            <Animated.View
              pointerEvents="none"
              style={{
                position: 'absolute',
                left: 20,
                paddingHorizontal: animatedFontSize.interpolate({ inputRange: [10, 16], outputRange: [5, 0] }),
                backgroundColor: StyleSheet.flatten(titleStyle ?? {}).backgroundColor,
                transform: [{ translateX: titleTranslate.x }, { translateY: titleTranslate.y }],
              }}
            >
              <Animated.Text style={animatedTitleStyleCombined}>{title}</Animated.Text>
            </Animated.View>
          )}
        </TouchableOpacity>
        <CharacterCount minLength={minLength} maxLength={maxLength} currentLength={value?.length ?? 0} showCharacterLimit={showCharacterLimit} multiline={multiline} />
      </View>
    );
  }
);

const MB_TextInput = memo((props: MB_TextInputProps) => {
  if (defaultProps) {
    const newProps: MB_TextInputDefaultProps = {};
    Object.keys(defaultProps).forEach(key => {
      const stylePropsKey = key as keyof MB_TextInputDefaultProps;
      if (stylePropsKey === 'placeholderTextColor') {
        newProps[stylePropsKey] = props[stylePropsKey] ?? defaultProps?.[stylePropsKey];
      } else {
        newProps[stylePropsKey] = props[stylePropsKey] ? [defaultProps?.[stylePropsKey], props[stylePropsKey]] : defaultProps?.[stylePropsKey];
      }
    });

    return <_MB_TextInput {...props} {...newProps} />;
  }

  return <_MB_TextInput {...props} />;
});

export { MB_TextInput, MB_TextInputSetDefaultStyles, MB_TextInputInputType, Masks };
export type { MB_TextInputRef, MB_TextInputDefaultProps, MB_TextInputProps, FormattedWithMaskType };

const baseStyles = StyleSheet.create({
  textInputStyle: StyleSheet.flatten([
    textStyles.normalText,
    {
      flexGrow: 1,
      textAlign: 'left',
      paddingLeft: 0,
      fontWeight: '400',
      paddingVertical: 0, // NOTE: Need this for Android since it automatically adds some padding
    },
  ]),
});

const styles = StyleSheet.create({
  container: {
    borderRadius: 6,
    paddingHorizontal: isMobile ? 10 : 20,
    alignItems: 'stretch',
    justifyContent: 'center',
    height: isMobile ? 44 : 54,
    width: 386,
    marginTop: 8,
    ...mbPlatformStyle({
      web: {
        cursor: 'text',
      },
    }),
  },
  securePasswordButton: {
    position: 'absolute',
    right: 0,
    margin: 0,
    paddingVertical: 4,
    paddingEnd: 12,
    paddingStart: 0,
    backgroundColor: 'transparent',
    height: '100%',
  },
  toolTipWrapper: {
    borderRadius: 8,
    backgroundColor: 'white',
    alignSelf: 'flex-start',
  },
  multilineTextInputStyle: StyleSheet.flatten([
    baseStyles.textInputStyle,
    {
      lineHeight: 20,
    },
  ]),
  defaultTitleStyle: StyleSheet.flatten([
    textStyles.smallText,
    {
      textAlign: 'left',
      marginBottom: 8,
    },
  ]),
  disabledTitle: {
    color: COLORS.lighterGray,
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  rightTitleStyle: StyleSheet.flatten([
    textStyles.smallText,
    {
      color: COLORS.dimViolet,
    },
  ]),
  remainingCharactersText: StyleSheet.flatten([
    textStyles.smallText,
    {
      fontSize: 12,
      color: COLORS.secondaryText,
      textAlign: 'left',
      marginTop: 4,
      minHeight: 12,
    },
  ]),
  outlineRemover: {
    ...mbPlatformStyle({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  marginNullified: {
    margin: 0,
    marginHorizontal: 0,
    marginVertical: 0,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
  },
  requiredAsteriksColor: {
    color: '#DD3636',
  },
});
